/**
 * MakeID PC Printer Integration for CampFlow
 * 
 * Uses the MakeID Label Print Server SDK (WewinPrintService) to print labels
 * directly from a desktop browser to a MakeID label printer.
 * 
 * Prerequisites:
 * - MakeID Label Print Server must be installed and running on the PC
 *   Download from: https://www.makeid.com/support
 * - A MakeID label printer must be connected and configured
 * 
 * The label templates replicate the exact same layout as the CampFlow
 * Android Printer app (see LabelTemplates.java).
 */

// ============================================================================
// TYPES
// ============================================================================

declare global {
  interface Window {
    jQuery: any;
    $: any;
    WewinPrintService: () => any;
  }
}

export interface PrinterInfo {
  printer: string;
  dots: number;
  hasDrive: string;
  USBID: string;
}

export interface PrintResult {
  success: boolean;
  errCode: number;
  errMsg: string;
  model?: string;
  sn?: string;
}

export interface AssetPrintData {
  code: string;
  qrContent: string;
  name?: string;
  brand?: string;
  model?: string;
  serialNumber?: string;
  purchaseDate?: string;
}

export interface LocationPrintData {
  code: string;
  qrContent: string;
  name: string;
  locationType?: string;
  capacity?: string;
  hasChildren?: boolean;
}

// ============================================================================
// SDK LOADER
// ============================================================================

let sdkLoaded = false;
let sdkLoading: Promise<void> | null = null;
let wps: any = null;

/**
 * Dynamically load a script and return a promise
 */
function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Impossibile caricare ${src}`));
    document.head.appendChild(script);
  });
}

/**
 * Load the MakeID Print SDK (jQuery + WewinPrintService)
 */
async function loadSDK(): Promise<void> {
  if (sdkLoaded && wps) return;

  if (sdkLoading) {
    await sdkLoading;
    return;
  }

  sdkLoading = (async () => {
    try {
      // Load jQuery first (required by WewinPrintService)
      if (!window.jQuery) {
        await loadScript('/js/jquery-3.7.1.min.js');
      }

      // Load WewinPrintService
      if (!window.WewinPrintService) {
        await loadScript('/js/WewinPrintService.js');
      }

      // Initialize the print service
      wps = window.WewinPrintService();
      wps.fontname = 'Arial';  // Default font for most text blocks
      wps.alert = false;  // Disable SDK alert dialogs — we handle errors in our UI
      wps.noViewTip = 'Stampante non trovata';  // Italian translation
      wps.noServiceTip = 'Installa il server di stampa MakeID o avvia il servizio';

      // Define our custom PageDoLabelPrint
      wps.PageDoLabelPrint = function () {
        wps.StartPrintTag(function (i: number) {
          const labelType = wps.ParseElement(i, 'LabelType');

          if (labelType[0] === 'asset_label') {
            const code = wps.ParseElement(i, 'Code');
            const qrContent = wps.ParseElement(i, 'QrContent');
            const brand = wps.ParseElement(i, 'Brand');
            const model = wps.ParseElement(i, 'Model');
            const serialNumber = wps.ParseElement(i, 'SerialNumber');
            const purchaseDate = wps.ParseElement(i, 'PurchaseDate');
            const name = wps.ParseElement(i, 'Name');
            const printDate = wps.ParseElement(i, 'PrintDate');
            wps.SetPrintFunc(i, drawAssetLabel, [code, qrContent, brand, model, serialNumber, purchaseDate, name, printDate]);

          } else if (labelType[0] === 'location_label') {
            const code = wps.ParseElement(i, 'Code');
            const qrContent = wps.ParseElement(i, 'QrContent');
            const locationType = wps.ParseElement(i, 'LocationType');
            const capacity = wps.ParseElement(i, 'Capacity');
            const hasChildren = wps.ParseElement(i, 'HasChildren');
            const name = wps.ParseElement(i, 'Name');
            const printDate = wps.ParseElement(i, 'PrintDate');
            wps.SetPrintFunc(i, drawLocationLabel, [code, qrContent, locationType, capacity, hasChildren, name, printDate]);
          }
        });
      };

      sdkLoaded = true;
    } catch (error) {
      sdkLoading = null;
      throw error;
    }
  })();

  await sdkLoading;
}

// ============================================================================
// LABEL DRAWING FUNCTIONS
// These replicate the exact layout from LabelTemplates.java
// ============================================================================

/**
 * Truncate text to fit within a given width in mm.
 * Uses the same estimate as the Android app: avgCharWidth = fontSize * 0.55
 */
function truncateText(text: string, availableWidthMm: number, fontSize: number): string {
  if (!text) return text;
  const avgCharWidth = fontSize * 0.55;
  const maxChars = Math.floor(availableWidthMm / avgCharWidth);
  if (text.length <= maxChars) return text;
  if (maxChars <= 3) return text.substring(0, Math.max(1, maxChars));
  return text.substring(0, maxChars - 3) + '...';
}

/**
 * Draw an Asset Label - replicates LabelTemplates.createAssetLabel()
 * 
 * Layout (50x25mm):
 * ┌─────────────────────────────────────────────────┐
 * │ ┌─────────┐  CODE (qr text fallback)  BOLD     │
 * │ │         │  Brand                              │
 * │ │   QR    │  Model                              │
 * │ │         │  SN: serial_number                  │
 * │ └─────────┘  Data acquisto                      │
 * │─────────────────────────────────────────────────│
 * │ Nome asset                   03/02/2026 22:14   │
 * └─────────────────────────────────────────────────┘
 */
function drawAssetLabel(
  code: string[],
  qrContent: string[],
  brand: string[],
  model: string[],
  serialNumber: string[],
  purchaseDate: string[],
  name: string[],
  printDate: string[]
) {
  const width = 50;
  const height = 25;
  const margin = 1.5;
  const qrSize = height - 8;             // = 17mm
  const textX = qrSize + margin + 2;     // = 20.5mm
  const textWidth = width - textX - margin; // = 28mm
  const bottomLineY = height - 6;        // = 19mm
  const lineHeight = 3;
  let currentY = margin;

  const label = new wps.Label();
  label.labelWidth = width;
  label.labelHeight = height;
  label.rotate = wps.rotate.rotate0;
  wps.StartTag(label);

  // === LEFT SIDE: QR Code ===
  if (qrContent[0]) {
    const qrcodeBlock = new wps.QrcodeBlock();
    qrcodeBlock.str = qrContent[0];
    qrcodeBlock.x = margin;
    qrcodeBlock.y = margin;
    qrcodeBlock.width = qrSize;
    qrcodeBlock.rotate = wps.rotate.rotate0;
    wps.PrintQrcode(qrcodeBlock);
  }

  // === RIGHT SIDE: Text fields ===

  // Row 1: Code - BOLD, larger (SimHei font)
  if (code[0]) {
    wps.fontname = 'SimHei';
    const textBlock = new wps.TextBlock();
    textBlock.str = [code[0]];
    textBlock.fontHeight = 5;
    textBlock.fontWeight = 700;
    textBlock.printWidth = textWidth;
    textBlock.x = textX;
    textBlock.y = currentY;
    textBlock.xoffset = 0.25;
    textBlock.loffset = 0.13;
    textBlock.rotate = wps.rotate.rotate0;
    textBlock.maxH = 5;
    textBlock.horizontal = wps.horizontal.left;
    textBlock.vertical = wps.vertical.top;
    textBlock.singleLine = true;
    wps.PrintText(textBlock);
    wps.fontname = 'Arial';
  }
  currentY += 5;

  // Row 2: Brand (Arial font)
  if (brand[0]) {
    const textBlock = new wps.TextBlock();
    textBlock.str = [brand[0]];
    textBlock.fontHeight = 3;
    textBlock.fontWeight = 500;
    textBlock.printWidth = textWidth;
    textBlock.x = textX;
    textBlock.y = currentY;
    textBlock.xoffset = 0.25;
    textBlock.loffset = 0.13;
    textBlock.rotate = wps.rotate.rotate0;
    textBlock.maxH = lineHeight;
    textBlock.horizontal = wps.horizontal.left;
    textBlock.vertical = wps.vertical.top;
    textBlock.singleLine = true;
    wps.PrintText(textBlock);
  }
  currentY += lineHeight;

  // Row 3: Model
  if (model[0]) {
    const textBlock = new wps.TextBlock();
    textBlock.str = [model[0]];
    textBlock.fontHeight = 3;
    textBlock.fontWeight = 500;
    textBlock.printWidth = textWidth;
    textBlock.x = textX;
    textBlock.y = currentY;
    textBlock.xoffset = 0.25;
    textBlock.loffset = 0.13;
    textBlock.rotate = wps.rotate.rotate0;
    textBlock.maxH = lineHeight;
    textBlock.horizontal = wps.horizontal.left;
    textBlock.vertical = wps.vertical.top;
    textBlock.singleLine = true;
    wps.PrintText(textBlock);
  }
  currentY += lineHeight;

  // Row 4: Serial Number
  if (serialNumber[0]) {
    const textBlock = new wps.TextBlock();
    textBlock.str = ['SN: ' + serialNumber[0]];
    textBlock.fontHeight = 2.5;
    textBlock.fontWeight = 500;
    textBlock.printWidth = textWidth;
    textBlock.x = textX;
    textBlock.y = currentY;
    textBlock.xoffset = 0.25;
    textBlock.loffset = 0.13;
    textBlock.rotate = wps.rotate.rotate0;
    textBlock.maxH = lineHeight;
    textBlock.horizontal = wps.horizontal.left;
    textBlock.vertical = wps.vertical.top;
    textBlock.singleLine = true;
    wps.PrintText(textBlock);
  }
  currentY += lineHeight;

  // Row 5: Purchase Date
  if (purchaseDate[0]) {
    const textBlock = new wps.TextBlock();
    textBlock.str = [purchaseDate[0]];
    textBlock.fontHeight = 2.5;
    textBlock.fontWeight = 500;
    textBlock.printWidth = textWidth;
    textBlock.x = textX;
    textBlock.y = currentY;
    textBlock.xoffset = 0.25;
    textBlock.loffset = 0.13;
    textBlock.rotate = wps.rotate.rotate0;
    textBlock.maxH = lineHeight;
    textBlock.horizontal = wps.horizontal.left;
    textBlock.vertical = wps.vertical.top;
    textBlock.singleLine = true;
    wps.PrintText(textBlock);
  }

  // === BOTTOM: Separator line + name + date ===

  // Horizontal separator line (full width)
  const lineBlock = new wps.LineBlock();
  lineBlock.x = margin;
  lineBlock.y = bottomLineY;
  lineBlock.ex = width - margin;
  lineBlock.ey = bottomLineY;
  lineBlock.thickness = 1;
  wps.PrintLine(lineBlock);

  // Bottom row layout
  const totalBottomWidth = width - (margin * 2);
  const dateReservedWidth = 24;
  const dateLeftShift = 4.5;
  const nameMaxWidth = totalBottomWidth - dateReservedWidth - 1;

  // Asset name (left, truncated if too long) - SimHei font
  if (name[0]) {
    wps.fontname = 'SimHei';
    const truncatedName = truncateText(name[0], nameMaxWidth, 2.5);
    const nameBlock = new wps.TextBlock();
    nameBlock.str = [truncatedName];
    nameBlock.fontHeight = 2.5;
    nameBlock.fontWeight = 500;
    nameBlock.printWidth = nameMaxWidth;
    nameBlock.x = margin;
    nameBlock.y = bottomLineY + 1;
    nameBlock.xoffset = 0.25;
    nameBlock.loffset = 0.13;
    nameBlock.rotate = wps.rotate.rotate0;
    nameBlock.maxH = 4;
    nameBlock.horizontal = wps.horizontal.left;
    nameBlock.vertical = wps.vertical.top;
    nameBlock.singleLine = true;
    wps.PrintText(nameBlock);
  }

  // Print date (right-aligned) - SimHei font
  if (printDate && printDate[0]) {
    wps.fontname = 'SimHei';
    const dateBlock = new wps.TextBlock();
    dateBlock.str = [printDate[0]];
    dateBlock.fontHeight = 2.5;
    dateBlock.fontWeight = 500;
    dateBlock.printWidth = dateReservedWidth;
    dateBlock.x = width - margin - dateReservedWidth - dateLeftShift;
    dateBlock.y = bottomLineY + 1;
    dateBlock.xoffset = 0.25;
    dateBlock.loffset = 0.13;
    dateBlock.rotate = wps.rotate.rotate0;
    dateBlock.maxH = 4;
    dateBlock.horizontal = wps.horizontal.right;
    dateBlock.vertical = wps.vertical.top;
    dateBlock.singleLine = true;
    wps.PrintText(dateBlock);
    wps.fontname = 'Arial';
  }

  wps.EndTag();
}

/**
 * Draw a Location Label - replicates LabelTemplates.createLocationLabel()
 * 
 * Layout (50x25mm) - same format as Asset Label:
 * ┌─────────────────────────────────────────────────┐
 * │ ┌─────────┐  CODE (qr text fallback)  BOLD     │
 * │ │         │  Tipo ubicazione                    │
 * │ │   QR    │  Capacità: X posti                  │
 * │ │         │  Sotto-ubicazioni: Sì/No            │
 * │ └─────────┘                                     │
 * │─────────────────────────────────────────────────│
 * │ Nome ubicazione              03/02/2026 22:14   │
 * └─────────────────────────────────────────────────┘
 */
function drawLocationLabel(
  code: string[],
  qrContent: string[],
  locationType: string[],
  capacity: string[],
  hasChildren: string[],
  name: string[],
  printDate: string[]
) {
  const width = 50;
  const height = 25;
  const margin = 1.5;
  const qrSize = height - 8;             // = 17mm
  const textX = qrSize + margin + 2;     // = 20.5mm
  const textWidth = width - textX - margin; // = 28mm
  const bottomLineY = height - 6;        // = 19mm
  const lineHeight = 3;
  let currentY = margin;

  const label = new wps.Label();
  label.labelWidth = width;
  label.labelHeight = height;
  label.rotate = wps.rotate.rotate0;
  wps.StartTag(label);

  // === LEFT SIDE: QR Code ===
  if (qrContent[0]) {
    const qrcodeBlock = new wps.QrcodeBlock();
    qrcodeBlock.str = qrContent[0];
    qrcodeBlock.x = margin;
    qrcodeBlock.y = margin;
    qrcodeBlock.width = qrSize;
    qrcodeBlock.rotate = wps.rotate.rotate0;
    wps.PrintQrcode(qrcodeBlock);
  }

  // === RIGHT SIDE: Text fields ===

  // Row 1: Code - BOLD, larger (SimHei font)
  if (code[0]) {
    wps.fontname = 'SimHei';
    const textBlock = new wps.TextBlock();
    textBlock.str = [code[0]];
    textBlock.fontHeight = 5;
    textBlock.fontWeight = 700;
    textBlock.printWidth = textWidth;
    textBlock.x = textX;
    textBlock.y = currentY;
    textBlock.xoffset = 0.25;
    textBlock.loffset = 0.13;
    textBlock.rotate = wps.rotate.rotate0;
    textBlock.maxH = 5;
    textBlock.horizontal = wps.horizontal.left;
    textBlock.vertical = wps.vertical.top;
    textBlock.singleLine = true;
    wps.PrintText(textBlock);
    wps.fontname = 'Arial';
  }
  currentY += 5;

  // Row 2: Location Type (Arial font)
  if (locationType[0]) {
    const textBlock = new wps.TextBlock();
    textBlock.str = [locationType[0]];
    textBlock.fontHeight = 3;
    textBlock.fontWeight = 500;
    textBlock.printWidth = textWidth;
    textBlock.x = textX;
    textBlock.y = currentY;
    textBlock.xoffset = 0.25;
    textBlock.loffset = 0.13;
    textBlock.rotate = wps.rotate.rotate0;
    textBlock.maxH = lineHeight;
    textBlock.horizontal = wps.horizontal.left;
    textBlock.vertical = wps.vertical.top;
    textBlock.singleLine = true;
    wps.PrintText(textBlock);
  }
  currentY += lineHeight;

  // Row 3: Capacity
  if (capacity[0]) {
    const textBlock = new wps.TextBlock();
    textBlock.str = [capacity[0]];
    textBlock.fontHeight = 3;
    textBlock.fontWeight = 500;
    textBlock.printWidth = textWidth;
    textBlock.x = textX;
    textBlock.y = currentY;
    textBlock.xoffset = 0.25;
    textBlock.loffset = 0.13;
    textBlock.rotate = wps.rotate.rotate0;
    textBlock.maxH = lineHeight;
    textBlock.horizontal = wps.horizontal.left;
    textBlock.vertical = wps.vertical.top;
    textBlock.singleLine = true;
    wps.PrintText(textBlock);
  }
  currentY += lineHeight;

  // Row 4: Sub-locations (Sì/No)
  {
    const hasChildrenBool = hasChildren[0] === 'true' || hasChildren[0] === 'Sì';
    const textBlock = new wps.TextBlock();
    textBlock.str = ['Sotto-ubicazioni: ' + (hasChildrenBool ? 'Sì' : 'No')];
    textBlock.fontHeight = 2.5;
    textBlock.fontWeight = 500;
    textBlock.printWidth = textWidth;
    textBlock.x = textX;
    textBlock.y = currentY;
    textBlock.xoffset = 0.25;
    textBlock.loffset = 0.13;
    textBlock.rotate = wps.rotate.rotate0;
    textBlock.maxH = lineHeight;
    textBlock.horizontal = wps.horizontal.left;
    textBlock.vertical = wps.vertical.top;
    textBlock.singleLine = true;
    wps.PrintText(textBlock);
  }

  // === BOTTOM: Separator line + name + date ===

  // Horizontal separator line (full width)
  const lineBlock = new wps.LineBlock();
  lineBlock.x = margin;
  lineBlock.y = bottomLineY;
  lineBlock.ex = width - margin;
  lineBlock.ey = bottomLineY;
  lineBlock.thickness = 1;
  wps.PrintLine(lineBlock);

  // Bottom row layout
  const totalBottomWidth = width - (margin * 2);
  const dateReservedWidth = 24;
  const dateLeftShift = 4.5;
  const nameMaxWidth = totalBottomWidth - dateReservedWidth - 1;

  // Location name (left, truncated if too long) - SimHei font
  if (name[0]) {
    wps.fontname = 'SimHei';
    const truncatedName = truncateText(name[0], nameMaxWidth, 2.5);
    const nameBlock = new wps.TextBlock();
    nameBlock.str = [truncatedName];
    nameBlock.fontHeight = 2.5;
    nameBlock.fontWeight = 500;
    nameBlock.printWidth = nameMaxWidth;
    nameBlock.x = margin;
    nameBlock.y = bottomLineY + 1;
    nameBlock.xoffset = 0.25;
    nameBlock.loffset = 0.13;
    nameBlock.rotate = wps.rotate.rotate0;
    nameBlock.maxH = 4;
    nameBlock.horizontal = wps.horizontal.left;
    nameBlock.vertical = wps.vertical.top;
    nameBlock.singleLine = true;
    wps.PrintText(nameBlock);
  }

  // Print date (right-aligned) - SimHei font
  if (printDate && printDate[0]) {
    wps.fontname = 'SimHei';
    const dateBlock = new wps.TextBlock();
    dateBlock.str = [printDate[0]];
    dateBlock.fontHeight = 2.5;
    dateBlock.fontWeight = 500;
    dateBlock.printWidth = dateReservedWidth;
    dateBlock.x = width - margin - dateReservedWidth - dateLeftShift;
    dateBlock.y = bottomLineY + 1;
    dateBlock.xoffset = 0.25;
    dateBlock.loffset = 0.13;
    dateBlock.rotate = wps.rotate.rotate0;
    dateBlock.maxH = 4;
    dateBlock.horizontal = wps.horizontal.right;
    dateBlock.vertical = wps.vertical.top;
    dateBlock.singleLine = true;
    wps.PrintText(dateBlock);
    wps.fontname = 'Arial';
  }

  wps.EndTag();
}

// ============================================================================
// PUBLIC API
// ============================================================================

const STORAGE_KEY_PRINTER = 'campflow_selected_printer';

/**
 * Check if we're on a desktop (non-mobile) device
 */
export function isDesktop(): boolean {
  if (typeof navigator === 'undefined') return false;
  return !/Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * Get the current date formatted as dd/MM/yyyy HH:mm (Italian locale)
 */
function getCurrentDateFormatted(): string {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yyyy = now.getFullYear();
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}

/**
 * Check if the MakeID Label Print Server is running
 */
export async function isServiceAvailable(): Promise<boolean> {
  try {
    await loadSDK();
    const printers = await getPrinters();
    return printers.length > 0;
  } catch {
    return false;
  }
}

/**
 * Get the list of available printers from the MakeID Label Print Server
 */
export async function getPrinters(): Promise<PrinterInfo[]> {
  await loadSDK();

  return new Promise((resolve, reject) => {
    wps.getPrinterList(
      (data: string) => {
        try {
          const parsed = typeof data === 'string' ? JSON.parse(data) : data;
          const printers: PrinterInfo[] = (parsed.content || []).map((p: any) => ({
            printer: p.printer,
            dots: p.dots,
            hasDrive: p.hasDrive,
            USBID: p.USBID || '',
          }));
          resolve(printers);
        } catch {
          resolve([]);
        }
      },
      (_error: any) => {
        reject(new Error('Il server di stampa MakeID non è raggiungibile. Assicurati che sia installato e in esecuzione.'));
      }
    );
  });
}

/**
 * Get the saved printer name from localStorage
 */
export function getSavedPrinter(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY_PRINTER);
  } catch {
    return null;
  }
}

/**
 * Save the selected printer name to localStorage
 */
export function savePrinter(printerName: string): void {
  try {
    localStorage.setItem(STORAGE_KEY_PRINTER, printerName);
  } catch {
    // localStorage not available
  }
}

/**
 * Print an asset label using the MakeID printer
 */
export async function printAssetLabelPC(
  data: AssetPrintData,
  printerName: string
): Promise<PrintResult> {
  await loadSDK();

  const printDate = getCurrentDateFormatted();

  const json = [{
    LabelType: 'asset_label',
    Code: data.code || '',
    QrContent: data.qrContent || '',
    Brand: data.brand || '',
    Model: data.model || '',
    SerialNumber: data.serialNumber || '',
    PurchaseDate: data.purchaseDate || '',
    Name: data.name || '',
    PrintDate: printDate,
  }];

  // Disable SDK alert dialogs — we handle errors ourselves
  wps.alert = false;

  return new Promise((resolve) => {
    wps.LabelPrint(json, {
      debug: false,
      noView: printerName,
    }, (result: any) => {
      if (result.errCode === 1 || result.errCode === 0) {
        resolve({
          success: true,
          errCode: result.errCode,
          errMsg: result.errCode === 0 ? 'Anteprima generata con successo' : 'Stampa completata con successo',
          model: result.model,
          sn: result.sn,
        });
      } else {
        resolve({
          success: false,
          errCode: result.errCode,
          errMsg: getItalianErrorMessage(result.errCode),
          model: result.model,
          sn: result.sn,
        });
      }
    });
  });
}

/**
 * Print a location label using the MakeID printer
 */
export async function printLocationLabelPC(
  data: LocationPrintData,
  printerName: string
): Promise<PrintResult> {
  await loadSDK();

  const printDate = getCurrentDateFormatted();

  const json = [{
    LabelType: 'location_label',
    Code: data.code || '',
    QrContent: data.qrContent || '',
    LocationType: data.locationType || '',
    Capacity: data.capacity || '',
    HasChildren: data.hasChildren ? 'true' : 'false',
    Name: data.name || '',
    PrintDate: printDate,
  }];

  // Disable SDK alert dialogs — we handle errors ourselves
  wps.alert = false;

  return new Promise((resolve) => {
    wps.LabelPrint(json, {
      debug: false,
      noView: printerName,
    }, (result: any) => {
      if (result.errCode === 1 || result.errCode === 0) {
        resolve({
          success: true,
          errCode: result.errCode,
          errMsg: result.errCode === 0 ? 'Anteprima generata con successo' : 'Stampa completata con successo',
          model: result.model,
          sn: result.sn,
        });
      } else {
        resolve({
          success: false,
          errCode: result.errCode,
          errMsg: getItalianErrorMessage(result.errCode),
          model: result.model,
          sn: result.sn,
        });
      }
    });
  });
}

/**
 * Translate error codes to Italian messages
 */
function getItalianErrorMessage(errCode: number): string {
  const messages: Record<number, string> = {
    0: 'Anteprima generata con successo',
    1: 'Stampa completata con successo',
    2: 'Stampa fallita - Etichetta non trovata',
    3: 'Stampa fallita - Nessun dato di stampa',
    4: 'Stampa fallita - Errore nei dati di stampa',
    5: 'Stampa fallita - Parametro HASDRIVE mancante',
    6: 'Stampa fallita - Parametro PRINTER mancante',
    7: 'Stampa fallita - Parametro COPYNUM mancante',
    8: 'Stampa fallita - Parametro HANDLETYPE mancante',
    9: 'Stampa fallita - Parametro COPYNUM vuoto',
    10: 'Stampa fallita - Verifica che la stampante sia collegata e accesa',
    11: 'Stampa fallita - Parametro LABELS mancante',
    12: 'Stampa fallita - Timeout nell\'attesa dei dati di stampa',
    13: 'Stampa fallita - Nessuna etichetta da stampare',
    14: 'Stampa fallita - Larghezza etichetta mancante',
    15: 'Stampa fallita - Altezza etichetta mancante',
    16: 'Stampa fallita - Errore inizializzazione RFID',
    17: 'Stampa fallita - Installa il server di stampa o avvia il servizio',
    18: 'Stampante non trovata. Verifica che la stampante sia collegata e accesa',
  };
  return messages[errCode] || `Stampa fallita - Verifica che la stampante sia collegata e accesa (codice: ${errCode})`;
}

/**
 * Get the download URL for the MakeID Label Print Server
 */
export function getPrintServerDownloadUrl(): string {
  return 'https://www.makeid.com/support';
}
